import type { User, UserTrainingPlan } from "../types";
import { DAY_MS, hasActiveSubscription, isPaused } from "./subscription";

/**
 * Inactivity block.
 *
 * A regular user who doesn't tick a single training day for INACTIVITY_DAYS
 * whole days can only use the chat (and settings) until a trainer unblocks
 * them. Example: last tick on Friday, nothing on Saturday, Sunday or Monday,
 * so blocked from Tuesday.
 *
 * Never blocked: staff, paused subscriptions, expired subscriptions (they have
 * their own lock), people without an active plan, and people whose app hasn't
 * started tracking yet (no `trainingCheckedAt`; the app sets it on the first
 * start of a version that tracks, so everyone gets the full 3 days).
 *
 * The count restarts from the latest of: the last tick, the start of the
 * newest active plan, and `inactivityReset` (trainer's unblock, resumed pause,
 * renewed subscription).
 */

export const INACTIVITY_DAYS = 3;

/**
 * Switched off on 2026-10-28: nobody is blocked any more. (Apps from before
 * this switch still have the block; functions/src/inactivity.ts keeps their
 * count pushed far into the future, so they don't block anyone either.)
 * The time of the last ticked training is still kept and shown to trainers.
 */
export const INACTIVITY_BLOCK_ENABLED = false;

/** The only screens a blocked user can open. */
export const INACTIVE_PATHS = ["/chat", "/settings"];

export const INACTIVITY_PHONE = "+385 99 2088 022";

const WEEK_MS = 7 * DAY_MS;

/** When a plan is shown as active, or null if it has no usable dates (then it's always active, like on the profile page). */
function planWindow(plan: UserTrainingPlan): { start: number; end: number } | null {
  if (plan.startWeek === undefined || plan.endWeek === undefined || !plan.assignedAt) return null;
  const assigned = Date.parse(plan.assignedAt);
  if (Number.isNaN(assigned)) return null;
  const startWeek = Number.isNaN(Number(plan.startWeek)) ? 0 : Number(plan.startWeek);
  const endWeek = Number.isNaN(Number(plan.endWeek)) ? 52 : Number(plan.endWeek);
  return { start: assigned + startWeek * WEEK_MS, end: assigned + endWeek * WEEK_MS };
}

function hasTraining(plan: UserTrainingPlan): boolean {
  return (plan.days || []).some((day) => (day.blocks || []).length > 0);
}

/** Active plans with at least one training in them. */
export function activeTrainingPlans(user: Pick<User, "trainingPlans">, now = Date.now()): UserTrainingPlan[] {
  return (user.trainingPlans || []).filter((plan) => {
    if (!plan || !hasTraining(plan)) return false;
    const window = planWindow(plan);
    return !window || (now > window.start && now < window.end);
  });
}

/** Midnight (device time) `days` days after the day of `ms`. */
function midnightAfter(ms: number, days: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return d.getTime();
}

function parse(iso: string | undefined): number {
  const ms = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(ms) ? 0 : ms;
}

export interface InactivityStatus {
  blocked: boolean;
  /** Whether the rule applies to this person at all right now. */
  tracked: boolean;
  /** When the count started (last tick, new plan or reset). */
  countingFrom?: Date;
  /** When the block starts (or started). */
  blockedFrom?: Date;
}

export function inactivityStatus(user: User | null | undefined, now = Date.now()): InactivityStatus {
  if (!INACTIVITY_BLOCK_ENABLED) return { blocked: false, tracked: false };
  if (!user || user.role !== "user") return { blocked: false, tracked: false };
  if (isPaused(user) || !hasActiveSubscription(user)) return { blocked: false, tracked: false };
  if (!user.trainingCheckedAt) return { blocked: false, tracked: false };
  const plans = activeTrainingPlans(user, now);
  if (plans.length === 0) return { blocked: false, tracked: false };

  const newestPlanStart = Math.max(0, ...plans.map((plan) => planWindow(plan)?.start ?? 0));
  const from = Math.max(
    parse(user.trainingCheckedAt),
    parse(user.inactivityReset?.at),
    newestPlanStart,
  );
  // Whole days without a tick: the day of the last tick doesn't count.
  const blockedFrom = midnightAfter(from, INACTIVITY_DAYS + 1);
  return {
    blocked: now >= blockedFrom,
    tracked: true,
    countingFrom: new Date(from),
    blockedFrom: new Date(blockedFrom),
  };
}

export function isBlockedForInactivity(user: User | null | undefined, now = Date.now()): boolean {
  return inactivityStatus(user, now).blocked;
}
